import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faFastForward } from '@fortawesome/free-solid-svg-icons';
import { GameState, League, MatchEvent, MatchEventType, Player, Result, TeamColors, TeamInLeague, TacticalPosition, Position } from '../../domain/entities';
import './SimulationOverlay.css';
import { getReadableAccentColor, parseColor, getContrast, calculateMatchPerformance, calculatePositionalPenalty } from '../../utils/utilities';
import { PlayerFace } from '../shared/PlayerFace';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { TeamBadge } from '../shared/TeamBadge';
import { FORMATIONS, POSITION_MAP, TACTICAL_ATTRIBUTES, calculateStarRating } from '../../domain/constants';

interface SimulationOverlayProps {
    title: string;
    playerMatchEvents: MatchEvent[] | null;
    result: Result | null;
    gameState: GameState;
    onDismiss: () => void;
    onCancel?: () => void; // Optional callback to cancel and return to tactics screen
    isPlayerMatch?: boolean; // Whether this is the player's team playing (default true)
    adsEnabled?: boolean; // Whether ads should be shown in the overlay
    onMatchStart?: () => void; // Called when the match actually starts playing (stage 3: game)
}

const getEventIcon = (type: MatchEventType) => {
    switch (type) {
        case 'goal':
            return <span className="event-icon">⚽️</span>;
        case 'penalty_scored':
            return <span className="event-icon">✓</span>;
        case 'penalty_missed':
            return <span className="event-icon">✗</span>;
        case 'yellow_card':
            return <span className="event-icon card yellow-card"></span>;
        case 'red_card':
            return <span className="event-icon card red-card"></span>;
        case 'info':
            return <span className="event-icon" />;
        default:
            return <span className="event-icon" />;
    }
};

const renderEventMessageWithColors = (message: string, homeTeamName: string, awayTeamName: string, homeColor: string, awayColor: string) => {
    // Split the message by team names and apply colors
    const parts: (string | React.ReactNode)[] = [];
    let lastIndex = 0;

    // Create a case-insensitive regex for team names
    const homeRegex = new RegExp(`\\b${homeTeamName}\\b`, 'gi');
    const awayRegex = new RegExp(`\\b${awayTeamName}\\b`, 'gi');

    // Track all matches with their positions
    const matches: { start: number; end: number; team: 'home' | 'away' }[] = [];

    let match;
    while ((match = homeRegex.exec(message)) !== null) {
        matches.push({ start: match.index, end: match.index + match[0].length, team: 'home' });
    }
    while ((match = awayRegex.exec(message)) !== null) {
        matches.push({ start: match.index, end: match.index + match[0].length, team: 'away' });
    }

    // Sort matches by position
    matches.sort((a, b) => a.start - b.start);

    // Build parts with colored team names
    for (const m of matches) {
        if (m.start > lastIndex) {
            parts.push(message.substring(lastIndex, m.start));
        }
        const teamName = message.substring(m.start, m.end);
        const color = m.team === 'home' ? homeColor : awayColor;
        parts.push(
            <span key={`${m.start}-${m.team}`} style={{ color }}>
                {teamName}
            </span>
        );
        lastIndex = m.end;
    }

    if (lastIndex < message.length) {
        parts.push(message.substring(lastIndex));
    }

    return parts.length > 0 ? parts : message;
};

const getSortedLineup = (team: TeamInLeague | undefined, startingXI_Ids: number[]): Player[] => {
    if (!team) return [];

    const formationPositions = FORMATIONS[team.tactics.formation] || [];
    const startingXI_Players = team.squad.filter(p => startingXI_Ids.includes(p.id));

    // Add any emergency players (ID < 0) from the result if not found in squad
    startingXI_Ids.forEach(id => {
        if (id < 0 && !startingXI_Players.some(p => p.id === id)) {
            startingXI_Players.push({ id, name: 'Youth Player', position: 'MID', skill: 25 } as Player);
        }
    });

    // Create a temporary lineup for sorting purposes, using only the players who actually played
    const tempLineup: Record<TacticalPosition, Player | null> = {};
    const availablePlayers = [...startingXI_Players];

    // 1. Fill positions with players of the correct type, best skill first
    formationPositions.forEach(pos => {
        const requiredGeneralPos = POSITION_MAP[pos];
        let bestPlayerIndex = -1;
        let bestSkill = -1;

        availablePlayers.forEach((player, index) => {
            if (player.position === requiredGeneralPos && player.skill > bestSkill) {
                bestSkill = player.skill;
                bestPlayerIndex = index;
            }
        });

        if (bestPlayerIndex !== -1) {
            tempLineup[pos] = availablePlayers[bestPlayerIndex];
            availablePlayers.splice(bestPlayerIndex, 1); // Remove player from available pool
        } else {
            tempLineup[pos] = null;
        }
    });

    // 2. Fill any remaining empty slots with the leftover players (who might be playing out of position)
    formationPositions.forEach(pos => {
        if (tempLineup[pos] === null && availablePlayers.length > 0) {
            // Sort remaining by skill to get the best one
            availablePlayers.sort((a, b) => b.skill - a.skill);
            tempLineup[pos] = availablePlayers.shift()!;
        }
    });

    // 3. Create the final sorted list based on the formation order
    return formationPositions
        .map(pos => tempLineup[pos])
        .filter((p): p is Player => p !== null && p !== undefined);
};

// Helper function to extract penalty events up to a certain index
const getPenaltyEvents = (events: MatchEvent[], upToIndex: number) => {
    return events
        .slice(0, upToIndex + 1)
        .filter(e => e.type === 'penalty_scored' || e.type === 'penalty_missed');
};

// Helper function to calculate current penalty scores from events
const calculatePenaltyScores = (events: MatchEvent[], teamNames: { home: string; away: string }) => {
    const homeScore = events.filter(e => e.type === 'penalty_scored' && e.team === teamNames.home).length;
    const awayScore = events.filter(e => e.type === 'penalty_scored' && e.team === teamNames.away).length;
    return { home: homeScore, away: awayScore };
};


export const SimulationOverlay: React.FC<SimulationOverlayProps> = ({ title, playerMatchEvents, result, gameState, onDismiss, onCancel, onMatchStart, isPlayerMatch = true, adsEnabled = true }) => {
    const [phase, setPhase] = useState<'lineup' | 'tactics' | 'game' | 'summary'>('lineup');
    const [events, setEvents] = useState<MatchEvent[]>([]);
    const [currentEventIndex, setCurrentEventIndex] = useState(-1);
    const [score, setScore] = useState({ home: 0, away: 0 });
    const [teamNames, setTeamNames] = useState({ home: '', away: '' });
    const [competition, setCompetition] = useState('');
    const [isMatchOver, setIsMatchOver] = useState(false);
    const listRef = useRef<HTMLUListElement>(null);
    const lastInteractionTime = useRef(0);
    const isTouchDevice = useRef<boolean>(false);
    
    // Detect if this is a touch device
    useEffect(() => {
        isTouchDevice.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }, []);
    
    const getPlayerById = useCallback((id: number): Player | undefined => {
        if (!gameState.gameStarted) return undefined;
        // FIX: Explicitly cast `Object.values(gameState.leagues)` to `League[]` to resolve type errors.
        for (const league of Object.values(gameState.leagues) as League[]) {
            for (const team of league.teams) {
                const player = team.squad.find(p => p.id === id);
                if (player) return player;
            }
        }
        return undefined;
    }, [gameState]);
    
    const allTeamsByName = useMemo(() => {
        if (!gameState.gameStarted) return new Map<string, TeamInLeague>();
        const map = new Map<string, TeamInLeague>();
        // FIX: Explicitly cast `Object.values(gameState.leagues)` to `League[]` to resolve type errors.
        (Object.values(gameState.leagues) as League[]).forEach(league => {
            league.teams.forEach(team => {
                map.set(team.name, team);
            });
        });
        return map;
    }, [gameState]);

    const REGULAR_SPEED = 1000;
    const CUP_FINAL_SPEED = 1500;
    const isCupFinal = useMemo(() => {
        // Check if this is a cup final by looking for " Final" at the end (e.g., "Junior Cup Final")
        // Also check for just "Final" in case the title format is different
        const compLower = competition.toLowerCase();
        return compLower.endsWith(' final') || compLower === 'final';
    }, [competition]);
    const speed = isCupFinal ? CUP_FINAL_SPEED : REGULAR_SPEED;
    
    const { homeTeam, awayTeam } = useMemo(() => {
        if (!gameState.gameStarted) return { homeTeam: undefined, awayTeam: undefined };
        // FIX: Explicitly cast `Object.values(gameState.leagues)` to `League[]` to resolve type errors.
        const allTeams = (Object.values(gameState.leagues) as League[]).flatMap(l => l.teams);
        return {
            homeTeam: allTeams.find(t => t.name === teamNames.home),
            awayTeam: allTeams.find(t => t.name === teamNames.away)
        };
    }, [gameState, teamNames]);

    const homeLineup = useMemo(() => result ? getSortedLineup(homeTeam, result.homeStartingXI) : [], [homeTeam, result]);
    const awayLineup = useMemo(() => result ? getSortedLineup(awayTeam, result.awayStartingXI) : [], [awayTeam, result]);
    
    const handleSkip = useCallback(() => {
        setCurrentEventIndex(events.length);
        const finalScoreEvent = [...events].reverse().find(e => e.isGoal);
        setScore({ home: finalScoreEvent?.homeScore || result?.homeScore || 0, away: finalScoreEvent?.awayScore || result?.awayScore || 0 });
    }, [events, result]);

    const handlePhaseAdvance = useCallback((e: React.MouseEvent) => {
        // Prevent advancement if clicking on a button or interactive element
        const target = e.target as HTMLElement;
        if (target.closest('button') || target.closest('[role="button"]')) {
            return;
        }

        // Advance to next phase based on current phase
        switch (phase) {
            case 'lineup':
                if (homeLineup.length > 0 && awayLineup.length > 0) {
                    setPhase('tactics');
                }
                break;
            case 'tactics':
                setPhase('game');
                onMatchStart?.();
                break;
            case 'game':
                // Skip to end of events and go to summary
                setCurrentEventIndex(events.length);
                const finalScoreEvent = [...events].reverse().find(e => e.isGoal);
                setScore({ home: finalScoreEvent?.homeScore || result?.homeScore || 0, away: finalScoreEvent?.awayScore || result?.awayScore || 0 });
                setPhase('summary');
                break;
            case 'summary':
                onDismiss();
                break;
        }
    }, [phase, homeLineup.length, awayLineup.length, events.length, result, onDismiss]);
    
    useEffect(() => {
        if (playerMatchEvents) {
            const [comp, matchTitle] = title.includes(' | ') ? title.split(' | ') : ['', title];
            const [home, away] = (matchTitle || title).split(' vs ');
            setCompetition(comp);
            setTeamNames({ home, away });
            setEvents(playerMatchEvents);
        } else {
            setEvents([]);
        }
        // Reset state for new match
        setPhase('lineup');
        setCurrentEventIndex(-1);
        setScore({ home: 0, away: 0 });
        setIsMatchOver(false);
    }, [playerMatchEvents, title]);
    
    // Phase transition controllers
    useEffect(() => {
        if (phase === 'game' && currentEventIndex === -1) {
            setCurrentEventIndex(0); // Start the game simulation
        }
    }, [phase, currentEventIndex]);

    // Game simulation logic (event-by-event) - AUTO-PROGRESSION ENABLED
    useEffect(() => {
        if (phase !== 'game' || currentEventIndex < 0 || currentEventIndex >= events.length) return;

        const currentEvent = events[currentEventIndex];
        if (currentEvent.isGoal && currentEvent.homeScore !== undefined) {
            setScore({ home: currentEvent.homeScore, away: currentEvent.awayScore! });
        }

        // Auto-progression: advance to next event after brief delay
        const timer = setTimeout(() => {
            setCurrentEventIndex(prev => prev + 1);
        }, 1200); // 1.2 second delay between events for readability
        return () => clearTimeout(timer);
    }, [phase, currentEventIndex, events]);
    
    useEffect(() => {
        if (currentEventIndex >= events.length && events.length > 0) {
            setIsMatchOver(true);
        }
    }, [currentEventIndex, events.length]);

    // Auto-advance to summary for cup finals when match ends
    useEffect(() => {
        if (isCupFinal && isMatchOver && phase === 'game') {
            setPhase('summary');
        }
    }, [isCupFinal, isMatchOver, phase]);

    useEffect(() => {
      if (listRef.current && currentEventIndex >= 0 && currentEventIndex < events.length) {
        const listChildren = listRef.current.children;
        if (listChildren.length > currentEventIndex) {
            const currentItem = listChildren[currentEventIndex] as HTMLLIElement;
            if (currentItem) {
              currentItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
      }
    }, [currentEventIndex, events]);
    
    const homePrimaryColor = homeTeam?.colors.primary || '#FFFFFF';
    const homeColor = getReadableAccentColor(homePrimaryColor, '#111111');
    let awayColorToUse = awayTeam?.colors.primary || '#FFFFFF';
    if (homeTeam && awayTeam) {
        const homeRgb = parseColor(homeTeam.colors.primary);
        const awayPrimaryRgb = parseColor(awayTeam.colors.primary);
        if (getContrast(homeRgb, awayPrimaryRgb) < 1.5) {
            awayColorToUse = awayTeam.colors.secondary;
        }
    }
    const awayColor = getReadableAccentColor(awayColorToUse, '#111111');

    // Convert hex colors to RGB strings for CSS rgba usage
    const homeRgb = parseColor(homePrimaryColor);
    const awayRgb = parseColor(awayColorToUse);
    const homeColorRgb = `${homeRgb[0]}, ${homeRgb[1]}, ${homeRgb[2]}`;
    const awayColorRgb = `${awayRgb[0]}, ${awayRgb[1]}, ${awayRgb[2]}`;

    const isSimulationProcessing = title.includes('Preparing') || title === '';

    if (!playerMatchEvents || !result) {
        return (
            <div 
                className="simulation-overlay"
                onClick={!isSimulationProcessing ? onDismiss : undefined}
                style={{ cursor: isSimulationProcessing ? 'wait' : 'pointer' }}
            >
                <div className="simulation-content">
                    <h2>{title || 'Simulating...'}</h2>
                    <p>{!isSimulationProcessing ? 'No match this week. Click to continue.' : 'Processing match results...'}</p>
                </div>
            </div>
        );
    }
    
    const winnerName = result.homeScore > result.awayScore ? teamNames.home : teamNames.away;
    const isDraw = result.homeScore === result.awayScore;


    return (
        <div className="simulation-overlay" onClick={handlePhaseAdvance} style={{ cursor: 'pointer' }}>
            <div className="simulation-content">
                {/* Unified Control Header - Always at Top */}
                <div className="simulation-controls-header">
                    <div className="phase-indicator">
                        <span className={`phase-badge ${phase === 'lineup' ? 'active' : 'complete'}`}>1. Teams</span>
                        <span className={`phase-badge ${phase === 'tactics' ? 'active' : phase === 'game' || phase === 'summary' ? 'complete' : ''}`}>2. Tactics</span>
                        <span className={`phase-badge ${phase === 'game' ? 'active' : phase === 'summary' ? 'complete' : ''}`}>3. Match</span>
                    </div>
                    <div className="control-buttons">
                        {phase === 'lineup' && (
                            <>
                                {homeLineup.length > 0 && awayLineup.length > 0 && (
                                    <button className="button primary" onClick={(e) => { e.stopPropagation(); setPhase('tactics'); }}>
                                        View Tactics →
                                    </button>
                                )}
                            </>
                        )}
                        {phase === 'tactics' && (
                            <>
                                {onCancel && isPlayerMatch && (
                                    <button className="button" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
                                        ← Change Tactics
                                    </button>
                                )}
                                <button className="button primary" onClick={(e) => { e.stopPropagation(); setPhase('game'); onMatchStart?.(); }}>
                                    Start Match →
                                </button>
                            </>
                        )}
                        {phase === 'game' && (
                            <>
                                {!isMatchOver && !isCupFinal && (
                                    <button className="button secondary" onClick={(e) => { e.stopPropagation(); handleSkip(); }}>
                                        <FontAwesomeIcon icon={faFastForward} style={{ marginRight: '8px' }} />
                                        Skip to Full Time
                                    </button>
                                )}
                                {isMatchOver && !isCupFinal && (
                                    <button className="button primary" onClick={(e) => { e.stopPropagation(); setPhase('summary'); }}>
                                        View Summary →
                                    </button>
                                )}
                            </>
                        )}
                        {phase === 'summary' && (
                            <button className="button primary" onClick={(e) => { e.stopPropagation(); onDismiss(); }}>
                                Close ✕
                            </button>
                        )}
                    </div>
                </div>
                <div className="simulation-scrollable">
                {phase === 'lineup' && (
                    <div className="lineup-phase-container">
                        <h3 className="competition-title">{isCupFinal ? `🏆 ${competition} 🏆` : competition}</h3>
                        <div className="lineup-header">
                            <h2 style={{ color: homeColor }}>{homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="md" />}{teamNames.home}</h2>
                            <span className="vs">vs</span>
                            <h2 style={{ color: awayColor }}>{awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="md" />}{teamNames.away}</h2>
                        </div>
                        <div className="lineup-columns" style={{ '--home-color-rgb': homeColorRgb, '--away-color-rgb': awayColorRgb } as React.CSSProperties}>
                            {Array.from({ length: Math.max(homeLineup.length, awayLineup.length) }).map((_, idx) => {
                                const homePlayer = homeLineup[idx];
                                const awayPlayer = awayLineup[idx];
                                const homeLineupPos = homePlayer ? Object.keys(homeTeam?.tactics.lineup || {}).find(pos => homeTeam?.tactics.lineup[pos as TacticalPosition] === homePlayer.id) : null;
                                const awayLineupPos = awayPlayer ? Object.keys(awayTeam?.tactics.lineup || {}).find(pos => awayTeam?.tactics.lineup[pos as TacticalPosition] === awayPlayer.id) : null;

                                return (
                                    <div key={`lineup-row-${idx}`} className="lineup-row">
                                        {/* Home Team Player */}
                                        {homePlayer ? (
                                            <>
                                                <div className="lineup-player-row home">
                                                    <span className="player-name">{homePlayer.name}</span>
                                                    <PlayerFace player={homePlayer} teamColor={homeColor} />
                                                </div>
                                                <span className="lineup-pos home">{homeLineupPos || homePlayer.position}</span>
                                            </>
                                        ) : (
                                            <div className="lineup-player-row home empty"></div>
                                        )}

                                        {/* Away Team Player */}
                                        {awayPlayer ? (
                                            <>
                                                <span className="lineup-pos away">{awayLineupPos || awayPlayer.position}</span>
                                                <div className="lineup-player-row away">
                                                    <PlayerFace player={awayPlayer} teamColor={awayColor} />
                                                    <span className="player-name">{awayPlayer.name}</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="lineup-player-row away empty"></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                )}
                {phase === 'tactics' && (() => {
                    // Calculate average performance by position
                    const calculateTeamPositionalStrength = (team: TeamInLeague) => {
                        const formationPositions = FORMATIONS[team.tactics.formation] || [];
                        const positionGroups: Record<Position, { players: Player[], positions: TacticalPosition[] }> = {
                            GK: { players: [], positions: [] },
                            DEF: { players: [], positions: [] },
                            MID: { players: [], positions: [] },
                            FWD: { players: [], positions: [] }
                        };

                        formationPositions.forEach(tactPos => {
                            const playerId = team.tactics.lineup[tactPos];
                            const player = team.squad.find(p => p.id === playerId);
                            const generalPos = POSITION_MAP[tactPos];

                            if (player && generalPos) {
                                positionGroups[generalPos].players.push(player);
                                positionGroups[generalPos].positions.push(tactPos);
                            }
                        });

                        return Object.entries(positionGroups).map(([pos, data]) => {
                            if (data.players.length === 0) return null;

                            const avgPerformance = data.players.reduce((sum, player, idx) => {
                                const basePerf = calculateMatchPerformance(player);
                                const penalty = calculatePositionalPenalty(player, data.positions[idx]);
                                return sum + (basePerf * (1 - penalty));
                            }, 0) / data.players.length;

                            return {
                                position: pos as Position,
                                count: data.players.length,
                                avgPerformance: Math.round(avgPerformance)
                            };
                        }).filter(Boolean);
                    };

                    const homeStrength = homeTeam ? calculateTeamPositionalStrength(homeTeam) : [];
                    const awayStrength = awayTeam ? calculateTeamPositionalStrength(awayTeam) : [];

                    const getPositionLabel = (pos: Position) => {
                        const labels = { GK: 'Goalkeeper', DEF: 'Def', MID: 'Mid', FWD: 'Fwd' };
                        return labels[pos];
                    };

                    return (
                        <>
                            <div className="tactics-phase-container">
                                <h3 className="competition-title">{isCupFinal ? `🏆 ${competition} 🏆` : competition}</h3>

                            {/* Team Headers with Formation */}
                            <div className="tactics-match-header">
                                <div className="tactics-team-info">
                                    <h2 style={{ color: homeColor }}>{homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="md" />}{teamNames.home}</h2>
                                    {homeTeam && (
                                        <div className="formation-badge">{homeTeam.tactics.formation}</div>
                                    )}
                                </div>
                                <span className="vs-large">VS</span>
                                <div className="tactics-team-info">
                                    <h2 style={{ color: awayColor }}>{awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="md" />}{teamNames.away}</h2>
                                    {awayTeam && (
                                        <div className="formation-badge">{awayTeam.tactics.formation}</div>
                                    )}
                                </div>
                            </div>

                            {/* Positional Strength Comparison */}
                            <div className="strength-comparison-section">
                                <h4>Match Performance by Position</h4>
                                <div className="strength-bars-container">
                                    {['GK', 'DEF', 'MID', 'FWD'].map(pos => {
                                        const homeData = homeStrength.find(s => s?.position === pos);
                                        const awayData = awayStrength.find(s => s?.position === pos);
                                        const homePerf = homeData?.avgPerformance || 0;
                                        const awayPerf = awayData?.avgPerformance || 0;
                                        const maxPerf = Math.max(homePerf, awayPerf, 1);
                                        const homeWidth = (homePerf / maxPerf) * 100;
                                        const awayWidth = (awayPerf / maxPerf) * 100;

                                        return (
                                            <div key={pos} className="strength-bar-row">
                                                <div className="strength-bar-wrapper home">
                                                    <span className="strength-value" style={{ color: homeColor }}>{homePerf}</span>
                                                    <div className="strength-bar-container">
                                                        <div className="strength-bar-fill" style={{ width: `${homeWidth}%`, backgroundColor: homeColor }} />
                                                    </div>
                                                </div>
                                                <div className="strength-label">
                                                    {getPositionLabel(pos as Position)}
                                                </div>
                                                <div className="strength-bar-wrapper away">
                                                    <div className="strength-bar-container">
                                                        <div className="strength-bar-fill" style={{ width: `${awayWidth}%`, backgroundColor: awayColor }} />
                                                    </div>
                                                    <span className="strength-value" style={{ color: awayColor }}>{awayPerf}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Tactical Approach Comparison */}
                            <div className="tactics-grid">
                                <div className="tactics-column">
                                    <div className="tactics-card" style={{ borderLeftColor: homeColor }}>
                                        {homeTeam && (
                                            <>
                                                <h3>Tactical Approach</h3>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">⚡</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Mentality</span>
                                                        <span className="tactic-value">{homeTeam.tactics.mentality}</span>
                                                    </div>
                                                </div>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">⚽</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Attack</span>
                                                        <span className="tactic-value">{homeTeam.tactics.attackingFocus}</span>
                                                    </div>
                                                </div>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">🎯</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Passing</span>
                                                        <span className="tactic-value">{homeTeam.tactics.passingStyle}</span>
                                                    </div>
                                                </div>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">🛡️</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Def</span>
                                                        <span className="tactic-value">{homeTeam.tactics.defensiveStyle}</span>
                                                    </div>
                                                </div>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">💪</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Tackling</span>
                                                        <span className="tactic-value">{homeTeam.tactics.tacklingStyle}</span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="tactics-column">
                                    <div className="tactics-card" style={{ borderLeftColor: awayColor }}>
                                        {awayTeam && (
                                            <>
                                                <h3>Tactical Approach</h3>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">⚡</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Mentality</span>
                                                        <span className="tactic-value">{awayTeam.tactics.mentality}</span>
                                                    </div>
                                                </div>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">⚽</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Attack</span>
                                                        <span className="tactic-value">{awayTeam.tactics.attackingFocus}</span>
                                                    </div>
                                                </div>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">🎯</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Passing</span>
                                                        <span className="tactic-value">{awayTeam.tactics.passingStyle}</span>
                                                    </div>
                                                </div>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">🛡️</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Def</span>
                                                        <span className="tactic-value">{awayTeam.tactics.defensiveStyle}</span>
                                                    </div>
                                                </div>
                                                <div className="tactic-row">
                                                    <span className="tactic-icon">💪</span>
                                                    <div className="tactic-info">
                                                        <span className="tactic-label">Tackling</span>
                                                        <span className="tactic-value">{awayTeam.tactics.tacklingStyle}</span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            </div>
                        </>
                    );
                })()}
                {phase === 'game' && (
                    <>
                        <div className="simulation-header">
                            <h3 className="competition-title">{isCupFinal ? `🏆 ${competition} 🏆` : competition}</h3>
                            <div className="match-title-grid" style={{ gridTemplateAreas: '"home-name score away-name"' }}>
                                <h2 className="team-name home" style={{ color: homeColor }}>{homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="md" />}{teamNames.home}</h2>
                                <div className="score">{score.home} - {score.away}</div>
                                <h2 className="team-name away" style={{ color: awayColor }}>{awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="md" />}{teamNames.away}</h2>
                            </div>
                            {/* Penalty shootout display */}
                            {(() => {
                                const penaltyEvents = getPenaltyEvents(events, currentEventIndex);
                                if (penaltyEvents.length > 0) {
                                    const penaltyScores = calculatePenaltyScores(penaltyEvents, teamNames);
                                    return (
                                        <div className="penalties-display">
                                            <div className="penalties-header">Penalty Shootout</div>
                                            <div className="penalties-content">
                                                <div className="penalties-score-section">
                                                    <div className="penalties-score">{penaltyScores.home}</div>
                                                    <span className="penalties-separator">-</span>
                                                    <div className="penalties-score">{penaltyScores.away}</div>
                                                </div>
                                                <div className="penalties-indicators">
                                                    <div className="team-penalties home-penalties">
                                                        {penaltyEvents
                                                            .filter(e => e.team === teamNames.home)
                                                            .map((event, idx) => (
                                                                <div
                                                                    key={`pen-home-${idx}`}
                                                                    className={`penalty-circle ${event.type === 'penalty_scored' ? 'scored' : 'missed'}`}
                                                                    title={event.type === 'penalty_scored' ? 'Scored' : 'Missed'}
                                                                />
                                                            ))}
                                                    </div>
                                                    <div className="team-penalties away-penalties">
                                                        {penaltyEvents
                                                            .filter(e => e.team === teamNames.away)
                                                            .map((event, idx) => (
                                                                <div
                                                                    key={`pen-away-${idx}`}
                                                                    className={`penalty-circle ${event.type === 'penalty_scored' ? 'scored' : 'missed'}`}
                                                                    title={event.type === 'penalty_scored' ? 'Scored' : 'Missed'}
                                                                />
                                                            ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                        <ul className="simulation-progress-list" ref={listRef}>
                            {events.map((event, index) => (
                                <li key={index} style={{ display: index > currentEventIndex ? 'none' : '' }}>
                                   {getEventIcon(event.type)}
                                   <span>{event.minute}' - {renderEventMessageWithColors(event.message, teamNames.home, teamNames.away, homeColor, awayColor)}</span>
                                </li>
                            ))}
                        </ul>

                    </>
                )}
                {phase === 'summary' && (
                    <div className="summary-phase-container">
                        <div className="simulation-header">
                            <div className="match-title-grid">
                                <h2 className="team-name home" style={{ color: homeColor }}>{homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="md" />}{teamNames.home}</h2>
                                <div className="score">
                                    <div>{result.homeScore} - {result.awayScore}</div>
                                    {result.penalties && (
                                        <div className="score-aet">
                                            <small>AET</small>
                                            <div className="penalties-score">{result.penalties.homeScore} - {result.penalties.awayScore}</div>
                                        </div>
                                    )}
                                </div>
                                <h2 className="team-name away" style={{ color: awayColor }}>{awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="md" />}{teamNames.away}</h2>
                                <div className="team-details home-details">
                                    <ul className="scorers-list">
                                        {result.events
                                            .filter(e => e.type === 'goal' && e.team === teamNames.home)
                                            .sort((a, b) => a.minute - b.minute)
                                            .map((event, i) => {
                                                const scorerNameMatch = event.message.match(/GOAL! (.*?) scores/);
                                                const scorerName = scorerNameMatch ? scorerNameMatch[1] : 'Unknown';
                                                return <li key={`g-h-${i}`}>⚽️ {scorerName} ({event.minute}')</li>;
                                            })}
                                        {result.yellowCards
                                            .filter(c => homeTeam?.squad.some(p => p.id === c.playerId))
                                            .sort((a, b) => a.minute - b.minute)
                                            .map((card, i) => {
                                                const player = getPlayerById(card.playerId);
                                                return player ? <li key={`yc-h-${i}`}><span className="event-icon card yellow-card" /> {player.name} ({card.minute}')</li> : null;
                                            })}
                                        {result.redCards
                                            .filter(c => homeTeam?.squad.some(p => p.id === c.playerId))
                                            .sort((a, b) => a.minute - b.minute)
                                            .map((card, i) => {
                                                const player = getPlayerById(card.playerId);
                                                return player ? <li key={`rc-h-${i}`}><span className="event-icon card red-card" /> {player.name} ({card.minute}')</li> : null;
                                            })}
                                        {result.events
                                            .filter(e => (e.type === 'penalty_scored' || e.type === 'penalty_missed') && e.team === teamNames.home)
                                            .sort((a, b) => a.minute - b.minute)
                                            .map((event, i) => {
                                                const penaltyPlayerMatch = event.message.match(/\[(.*?)\]\s(.*?)(?:\sscores|\smisses)/);
                                                const penaltyPlayer = penaltyPlayerMatch ? penaltyPlayerMatch[2] : 'Unknown';
                                                const scored = event.type === 'penalty_scored';
                                                return <li key={`pen-h-${i}`}><span className="event-icon">{scored ? '✓' : '✗'}</span> {penaltyPlayer} ({event.minute}')</li>;
                                            })}
                                    </ul>
                                </div>
                                <div className="team-details away-details">
                                    <ul className="scorers-list">
                                        {result.events
                                            .filter(e => e.type === 'goal' && e.team === teamNames.away)
                                            .sort((a, b) => a.minute - b.minute)
                                            .map((event, i) => {
                                                const scorerNameMatch = event.message.match(/GOAL! (.*?) scores/);
                                                const scorerName = scorerNameMatch ? scorerNameMatch[1] : 'Unknown';
                                                return <li key={`g-a-${i}`}>⚽️ {scorerName} ({event.minute}')</li>;
                                            })}
                                        {result.yellowCards
                                            .filter(c => awayTeam?.squad.some(p => p.id === c.playerId))
                                            .sort((a, b) => a.minute - b.minute)
                                            .map((card, i) => {
                                                const player = getPlayerById(card.playerId);
                                                return player ? <li key={`yc-a-${i}`}><span className="event-icon card yellow-card" /> {player.name} ({card.minute}')</li> : null;
                                            })}
                                        {result.redCards
                                            .filter(c => awayTeam?.squad.some(p => p.id === c.playerId))
                                            .sort((a, b) => a.minute - b.minute)
                                            .map((card, i) => {
                                                const player = getPlayerById(card.playerId);
                                                return player ? <li key={`rc-a-${i}`}><span className="event-icon card red-card" /> {player.name} ({card.minute}')</li> : null;
                                            })}
                                        {result.events
                                            .filter(e => (e.type === 'penalty_scored' || e.type === 'penalty_missed') && e.team === teamNames.away)
                                            .sort((a, b) => a.minute - b.minute)
                                            .map((event, i) => {
                                                const penaltyPlayerMatch = event.message.match(/\[(.*?)\]\s(.*?)(?:\sscores|\smisses)/);
                                                const penaltyPlayer = penaltyPlayerMatch ? penaltyPlayerMatch[2] : 'Unknown';
                                                const scored = event.type === 'penalty_scored';
                                                return <li key={`pen-a-${i}`}><span className="event-icon">{scored ? '✓' : '✗'}</span> {penaltyPlayer} ({event.minute}')</li>;
                                            })}
                                    </ul>
                                </div>
                            </div>
                        </div>
                         {result.potm && (() => {
                            const player = getPlayerById(result.potm!.playerId);
                            const team = allTeamsByName.get(result.potm!.teamName);
                            const potmTeamColor = team ? getReadableAccentColor(team.colors.primary, '#111111') : 'var(--text-color)';
                            return player && (
                                <div className="potm-display">
                                    <div className="potm-header"><FontAwesomeIcon icon={faStar} /> Player of the Match <FontAwesomeIcon icon={faStar} /></div>
                                    <div className="potm-player">
                                        <PlayerFace player={player} teamColor={team?.colors.primary} />
                                        <div className="potm-player-info">
                                            <div>
                                                <span className="potm-player-name">{player.name}</span>{' '}
                                                <span className="potm-player-team" style={{ color: potmTeamColor }}>({result.potm!.teamName})</span>
                                            </div>
                                            <span className="potm-player-rating">Rating: {result.potm!.rating.toFixed(1)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
                </div>

                {/* Ad Section */}
                {adsEnabled && (
                    <div className="simulation-ad-section">
                        <AdPlaceholder format="horizontal" testMode={false} />
                    </div>
                )}
            </div>

            {phase === 'summary' && isCupFinal && !isDraw && (
                <>
                    <div className="tickertape">
                        {Array.from({ length: 15 }).map((_, i) => <div key={i} className="confetti" />)}
                    </div>
                    <div className="cup-final-winner-celebration">
                        <div className="cup-emoji">🏆</div>
                        <h3 className="winner-announcement">{winnerName} win the {competition}!</h3>
                    </div>
                </>
            )}
        </div>
    );
};